import { addTime, formatDate, percent } from '@perch-labs/warbler';
import { CleanKwhs } from 'contexts/clean-kwh/clean-kwh.typings';
import { copyFor } from 'config';

interface GraphNode {
  x: string;
  y: number | null;
}
type GraphNodes = Array<GraphNode>;

const getCopy = copyFor('modules.usageGraph');

interface CalculateMaxAndMinXAxisDatesParam {
  cleanKwhsSinceLatest: number;
  graphData: GraphNodes;
  xAxisMonthRange: number;
}

export const calculateMaxAndMinXAxisDates = ({
  cleanKwhsSinceLatest,
  graphData,
  xAxisMonthRange,
}: CalculateMaxAndMinXAxisDatesParam): { max: number; min: number } => {
  // @ts-expect-error ts-migrate(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
  graphData.sort((a, b) => new Date(b.x) - new Date(a.x));
  let rangeMaxDate = firstOfMonth(
    addTime(new Date(graphData[0].x), { months: 1 - cleanKwhsSinceLatest })
  );
  let rangeMinDate = addTime(rangeMaxDate, {
    months: -(xAxisMonthRange - 1),
  });
  if (graphData.length - cleanKwhsSinceLatest < xAxisMonthRange - 1) {
    rangeMinDate = addTime(new Date(graphData[graphData.length - 1].x), {
      months: -1,
    });
    rangeMaxDate = addTime(rangeMinDate, { months: xAxisMonthRange - 1 });
  }
  // hack: extend range to show min and max labels
  const max = addTime(rangeMaxDate, { days: 9 }).getTime();
  const min = addTime(firstOfMonth(rangeMinDate), { days: -9 }).getTime();
  return { max, min };
};

interface CalculateXAxisMonthPositionParam {
  cleanKwhsSinceLatest: number;
  defaultXAxisMonthPosition: number;
  graphData: GraphNodes;
}

export const calculateXAxisMonthPosition = ({
  cleanKwhsSinceLatest,
  defaultXAxisMonthPosition,
  graphData,
}: CalculateXAxisMonthPositionParam): number =>
  graphData.length - cleanKwhsSinceLatest < defaultXAxisMonthPosition
    ? graphData.length - cleanKwhsSinceLatest + 1
    : defaultXAxisMonthPosition;

export const firstOfMonth = (date: Date): Date => {
  const dateObj = typeof date.getMonth === 'function' ? date : new Date(date);
  dateObj.setDate(1);
  return dateObj;
};

const dateFormat = 'MM/dd/yy';

export const processGraphData = (data: CleanKwhs): GraphNodes =>
  data
    .filter(item => item.kwh !== 0)
    .map(item => ({
      x: formatDate(item.startDate, { format: dateFormat }),
      y: item.kwh,
    }));

export const calculateYAxisMaximum = (data: GraphNodes): number => {
  const max =
    data?.reduce((acc, item) => {
      const yValue = item.y || 0;
      if (acc < yValue) acc = yValue;
      return acc;
    }, 0) || 0;
  return max * 1.2;
};

const percentChange = (a: number, b: number) => Math.abs(a - b) / b;

interface CalculatePercentChangeParam {
  cleanKwhIndex: number;
  cleanKwhs: CleanKwhs;
}

export const calculatePercentChange = ({
  cleanKwhIndex,
  cleanKwhs,
}: CalculatePercentChangeParam): {
  adj: string;
  value: string | null;
} | null => {
  const current = cleanKwhs[cleanKwhIndex]?.kwh || 0;
  const previous = cleanKwhs[cleanKwhIndex - 1]?.kwh;
  if (!previous) return null;
  const adj = current > previous ? 'more' : 'less';
  return { adj, value: percent(percentChange(current, previous), 0) };
};

interface GenerateDescriptionParam {
  cleanKwh: CleanKwhs[number];
  cleanKwhIndex: number;
  cleanKwhs: CleanKwhs;
  connectedWind: { isActive: boolean; isUnavailable: boolean };
}

export const generateDescription = ({
  cleanKwh,
  cleanKwhIndex,
  cleanKwhs,
  connectedWind,
}: GenerateDescriptionParam): string => {
  const { isActive, isUnavailable } = connectedWind;
  const percentChange = isActive
    ? calculatePercentChange({ cleanKwhIndex, cleanKwhs })
    : null;
  const descriptionParams = {
    endDate: formatDate(cleanKwh.endDate, { format: 'MMM d' }),
    percentChange: percentChange?.value,
    percentChangeAdj: percentChange?.adj,
    startDate: formatDate(cleanKwh.startDate, { format: 'MMM d' }),
  };

  let description = getCopy('description', descriptionParams);
  if (isActive) {
    if (percentChange)
      description = getCopy('descriptionConnected', descriptionParams);
    else description = getCopy('descriptionConnectedFirstMonth');
  } else if (isUnavailable) {
    description = getCopy('descriptionUnavailable', descriptionParams);
  }

  return description;
};

interface GenerateGraphConfigParam {
  cleanKwh: CleanKwhs[number];
  cleanKwhsSinceLatest: number;
  theme: any;
}

export const generateGraphConfig = ({
  cleanKwh,
  cleanKwhsSinceLatest,
  theme,
}: GenerateGraphConfigParam): any => ({
  grid: {
    xaxis: { lines: { show: true } },
    yaxis: { lines: { show: false } },
  },
  markers: {
    discrete: [
      {
        dataPointIndex: cleanKwhsSinceLatest,
        fillColor: 'white',
        seriesIndex: 0,
        size: 6,
        strokeColor: theme.colors.alternative1,
      },
    ],
    size: 6,
    strokeColor: theme.colors.alternative1,
    strokeWidth: 4,
  },
  title: {
    margin: 0,
    offsetX: -10,
    offsetY: 10,
    style: {
      color: theme.colors.alternative3,
    },
    text: formatDate(cleanKwh.startDate, { format: 'yyyy' }),
  },
  tooltip: {
    x: {
      format: 'MMMM d',
    },
    y: {
      formatter: (value: number) => `${value} kWh`,
      title: {
        formatter: () => '',
      },
    },
  },
});
